import { useState, useCallback } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import axios from '../axiosConfig';
import { STRIPE_PUBLIC_KEY, API_ENDPOINTS } from '../utils/constants';

// Initialize Stripe
const stripePromise = loadStripe(STRIPE_PUBLIC_KEY);

/**
 * Custom hook for subscription API operations
 * Handles all subscription-related API calls
 */
export const useSubscriptionApi = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    /**
     * Clear current error
     */
    const clearError = useCallback(() => {
        setError(null);
    }, []);

    /**
     * Fetch available subscription plans/products
     * @returns {Promise<Array>} Formatted plans array
     */
    const fetchPlans = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            const response = await axios.get(API_ENDPOINTS.PRODUCTS);

            if (response.data.error) {
                throw new Error(response.data.error);
            }

            // Format plans from API response
            const formattedPlans = response.data.map(product => {
                const price = product.prices?.[0] || null;
                return {
                    id: product.id,
                    priceId: price?.id || null,
                    name: product.name,
                    description: product.description,
                    price: price ? price.unitAmount / 100 : 0,
                    currency: price?.currency || 'pln',
                    period: price?.recurring?.interval || 'month',
                    features: product.features || [],
                    recommended: product.recommended || false
                };
            });

            return formattedPlans;
        } catch (err) {
            console.error('Error fetching plans:', err);
            const errorMessage = err.response?.status === 429
                ? 'Zbyt wiele zapytań. Odśwież stronę za chwilę.'
                : 'Nie udało się pobrać planów subskrypcji.';
            setError(errorMessage);
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    /**
     * Create checkout session and redirect to Stripe
     * @param {string} priceId - Stripe price ID
     * @returns {Promise<void>}
     */
    const createCheckoutSession = useCallback(async (priceId) => {
        try {
            setLoading(true);
            setError(null);

            const response = await axios.post(API_ENDPOINTS.CREATE_CHECKOUT, {
                priceId,
                quantity: 1
            });

            if (response.data.error) {
                throw new Error(response.data.error);
            }

            const { sessionId, url } = response.data;

            // Redirect to Stripe checkout
            if (url) {
                window.location.href = url;
            } else if (sessionId) {
                const stripe = await stripePromise;
                const { error: stripeError } = await stripe.redirectToCheckout({ sessionId });
                if (stripeError) {
                    throw new Error(stripeError.message);
                }
            }
        } catch (err) {
            console.error('Error creating checkout session:', err);
            const errorMessage = err.response?.status === 429
                ? 'Zbyt wiele prób. Poczekaj chwilę.'
                : err.message || 'Nie udało się przetworzyć żądania.';
            setError(errorMessage);
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    /**
     * Change subscription plan
     * @param {string} priceId - New price ID
     * @returns {Promise<Object>} Response data
     */
    const changePlan = useCallback(async (priceId) => {
        try {
            setLoading(true);
            setError(null);

            const response = await axios.post(API_ENDPOINTS.CHANGE_PLAN, {
                priceId,
                enableProration: true
            });

            if (response.data.error) {
                throw new Error(response.data.error);
            }

            return response.data;
        } catch (err) {
            console.error('Error changing plan:', err);
            const errorMessage = err.response?.status === 429
                ? 'Zbyt wiele prób. Poczekaj chwilę.'
                : err.message || 'Nie udało się zmienić planu.';
            setError(errorMessage);
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    /**
     * Cancel subscription
     * @returns {Promise<Object>} Response data
     */
    const cancelSubscription = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            const response = await axios.post(API_ENDPOINTS.CANCEL);

            if (response.data.error) {
                throw new Error(response.data.error);
            }

            return response.data;
        } catch (err) {
            console.error('Error cancelling subscription:', err);
            setError(err.message || 'Nie udało się anulować subskrypcji.');
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    /**
     * Open Stripe customer portal
     * @returns {Promise<void>}
     */
    const openCustomerPortal = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            const response = await axios.post(API_ENDPOINTS.CUSTOMER_PORTAL);

            if (response.data.url) {
                window.location.href = response.data.url;
            }
        } catch (err) {
            console.error('Error opening customer portal:', err);
            setError('Nie udało się otworzyć portalu płatności.');
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    /**
     * Start free trial
     * @returns {Promise<Object>} Response data
     */
    const startTrial = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            const response = await axios.post(API_ENDPOINTS.START_TRIAL);

            if (response.data.error) {
                throw new Error(response.data.error);
            }

            return response.data;
        } catch (err) {
            console.error('Error starting trial:', err);
            
            let errorMessage;
            if (err.response?.status === 429) {
                errorMessage = 'Zbyt wiele prób. Proszę poczekać chwilę.';
            } else if (err.response?.status === 400) {
                errorMessage = err.response?.data?.error || 'Nie można rozpocząć okresu próbnego.';
            } else {
                errorMessage = err.response?.data?.error || 'Nie udało się rozpocząć okresu próbnego.';
            }
            
            setError(errorMessage);
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    return {
        loading,
        error,
        clearError,
        fetchPlans,
        createCheckoutSession,
        changePlan,
        cancelSubscription,
        openCustomerPortal,
        startTrial
    };
};

export default useSubscriptionApi;
